import React, { useState } from 'react';
import { db, storage } from '../firebase/config';
import { collection, addDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { X, RotateCcw, Camera, Loader, AlertCircle, CheckCircle } from 'lucide-react';

const REQUEST_TYPES = [
    { key: 'refund', label: '환불', description: '결제 금액을 환불받고 싶습니다' },
    { key: 'return', label: '반품', description: '상품을 반품하고 싶습니다' },
    { key: 'exchange', label: '교환', description: '다른 상품으로 교환하고 싶습니다' }
];

const REASON_CATEGORIES = [
    { key: 'defective', label: '불량' },
    { key: 'wrong_item', label: '오배송' },
    { key: 'change_of_mind', label: '단순변심' },
    { key: 'not_as_described', label: '상품불일치' },
    { key: 'other', label: '기타' }
];

function RefundRequest({ userInfo, order, onClose, onSubmitted }) {
    const [type, setType] = useState('refund');
    const [reasonCategory, setReasonCategory] = useState('');
    const [description, setDescription] = useState('');
    const [images, setImages] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const handleImageUpload = (e) => {
        const files = Array.from(e.target.files);
        if (images.length + files.length > 5) {
            setError('이미지는 최대 5장까지 업로드할 수 있습니다.');
            return;
        }

        const validFiles = files.filter(file => {
            if (file.size > 5 * 1024 * 1024) {
                setError('각 이미지는 5MB 이하여야 합니다.');
                return false;
            }
            if (!file.type.startsWith('image/')) {
                setError('이미지 파일만 업로드 가능합니다.');
                return false;
            }
            return true;
        });

        setImages(prev => [...prev, ...validFiles]);

        validFiles.forEach(file => {
            const reader = new FileReader();
            reader.onload = (ev) => {
                setImagePreviews(prev => [...prev, ev.target.result]);
            };
            reader.readAsDataURL(file);
        });
    };

    const removeImage = (index) => {
        setImages(prev => prev.filter((_, i) => i !== index));
        setImagePreviews(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async () => {
        if (!reasonCategory) {
            setError('사유를 선택해주세요.');
            return;
        }
        if (!description.trim()) {
            setError('상세 설명을 입력해주세요.');
            return;
        }

        setSubmitting(true);
        setError('');

        try {
            // Upload images
            const uploadedUrls = [];
            for (const file of images) {
                const fileName = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}_${file.name}`;
                const storageRef = ref(storage, `refundRequests/${order.id || order.orderId}/${fileName}`);
                await uploadBytes(storageRef, file);
                const url = await getDownloadURL(storageRef);
                uploadedUrls.push(url);
            }

            const typeLabels = { refund: '환불', return: '반품', exchange: '교환' };
            const refundData = {
                orderId: order.id || order.orderId,
                buyerId: userInfo.uid,
                sellerId: order.sellerId,
                type,
                reasonCategory,
                description: description.trim(),
                images: uploadedUrls,
                amount: order.payment?.total || order.totalAmount || 0,
                status: 'requested',
                statusHistory: [{
                    status: 'requested',
                    timestamp: new Date(),
                    message: `${typeLabels[type]} 요청`
                }],
                createdAt: new Date()
            };

            await addDoc(collection(db, 'refundRequests'), refundData);

            if (onSubmitted) onSubmitted();
            if (onClose) onClose();
        } catch (err) {
            console.error('환불/반품 요청 실패:', err);
            setError('요청 등록에 실패했습니다. 다시 시도해주세요.');
        } finally {
            setSubmitting(false);
        }
    };

    const selectedType = REQUEST_TYPES.find(t => t.key === type);

    return (
        <div style={styles.overlay} onClick={onClose}>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div style={styles.header}>
                    <div style={styles.headerLeft}>
                        <RotateCcw size={24} color="#6366f1" />
                        <h2 style={styles.headerTitle}>환불/반품 요청</h2>
                    </div>
                    <button style={styles.closeBtn} onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div style={styles.body}>
                    {error && (
                        <div style={styles.errorBox}>
                            <AlertCircle size={18} color="#ef4444" />
                            <span>{error}</span>
                        </div>
                    )}

                    {/* Order Info Summary */}
                    <div style={styles.orderSummary}>
                        <span style={styles.orderSummaryLabel}>주문번호</span>
                        <span style={styles.orderSummaryValue}>{order.id || order.orderId}</span>
                    </div>

                    {/* Request Type */}
                    <div style={styles.fieldGroup}>
                        <label style={styles.label}>요청 유형</label>
                        <div style={styles.typeOptions}>
                            {REQUEST_TYPES.map(t => (
                                <button
                                    key={t.key}
                                    style={{
                                        ...styles.typeButton,
                                        ...(type === t.key ? styles.typeButtonActive : {})
                                    }}
                                    onClick={() => setType(t.key)}
                                >
                                    <span style={{
                                        ...styles.typeLabel,
                                        color: type === t.key ? '#6366f1' : '#475569'
                                    }}>
                                        {t.label}
                                    </span>
                                    <span style={{
                                        ...styles.typeDesc,
                                        color: type === t.key ? '#818cf8' : '#94a3b8'
                                    }}>
                                        {t.description}
                                    </span>
                                    {type === t.key && (
                                        <CheckCircle size={18} color="#6366f1" style={{ position: 'absolute', top: '8px', right: '8px' }} />
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Reason Category */}
                    <div style={styles.fieldGroup}>
                        <label style={styles.label}>사유 선택</label>
                        <select
                            value={reasonCategory}
                            onChange={(e) => setReasonCategory(e.target.value)}
                            style={styles.select}
                        >
                            <option value="">사유를 선택해주세요</option>
                            {REASON_CATEGORIES.map(cat => (
                                <option key={cat.key} value={cat.key}>{cat.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* Description */}
                    <div style={styles.fieldGroup}>
                        <label style={styles.label}>상세 설명</label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="요청 사유를 상세히 설명해주세요"
                            style={styles.textarea}
                            maxLength={1000}
                            rows={5}
                        />
                        <span style={styles.charCount}>{description.length}/1000</span>
                    </div>

                    {/* Image Upload */}
                    <div style={styles.fieldGroup}>
                        <label style={styles.label}>증빙 사진 ({images.length}/5)</label>
                        <p style={styles.imageHint}>불량/파손 등의 경우 관련 사진을 첨부해주세요</p>
                        <div style={styles.imageUploadArea}>
                            {imagePreviews.map((preview, index) => (
                                <div key={index} style={styles.imagePreviewWrapper}>
                                    <img src={preview} alt={`preview-${index}`} style={styles.imagePreview} />
                                    <button
                                        style={styles.imageRemoveBtn}
                                        onClick={() => removeImage(index)}
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                            ))}
                            {images.length < 5 && (
                                <label style={styles.imageAddBtn}>
                                    <Camera size={24} color="#94a3b8" />
                                    <span style={styles.imageAddText}>추가</span>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        onChange={handleImageUpload}
                                        style={{ display: 'none' }}
                                    />
                                </label>
                            )}
                        </div>
                    </div>

                    {/* Refund Amount */}
                    <div style={styles.amountBox}>
                        <span style={styles.amountLabel}>환불 예정 금액</span>
                        <span style={styles.amountValue}>
                            {(order.payment?.total || order.totalAmount || 0).toLocaleString()}원
                        </span>
                    </div>

                    {/* Notice */}
                    <div style={styles.noticeBox}>
                        <p style={styles.noticeText}>
                            - 단순변심의 경우 반품 배송비가 발생할 수 있습니다.
                        </p>
                        <p style={styles.noticeText}>
                            - 판매자 확인 후 처리되며, 영업일 기준 1-3일 소요됩니다.
                        </p>
                        <p style={styles.noticeText}>
                            - 환불은 원결제 수단으로 진행됩니다.
                        </p>
                    </div>
                </div>

                {/* Footer */}
                <div style={styles.footer}>
                    <button style={styles.cancelBtn} onClick={onClose} disabled={submitting}>
                        취소
                    </button>
                    <button
                        style={{
                            ...styles.submitBtn,
                            ...(submitting ? styles.submitBtnDisabled : {})
                        }}
                        onClick={handleSubmit}
                        disabled={submitting}
                    >
                        {submitting ? (
                            <>
                                <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                                요청 중...
                            </>
                        ) : (
                            '요청하기'
                        )}
                    </button>
                </div>
            </div>

            <style>{`
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
            `}</style>
        </div>
    );
}

const styles = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        zIndex: 1000
    },
    modal: {
        width: '100%',
        maxWidth: '600px',
        maxHeight: '90vh',
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '20px 24px',
        borderBottom: '1px solid #e2e8f0'
    },
    headerLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
    },
    headerTitle: {
        margin: 0,
        fontSize: '20px',
        fontWeight: '700',
        color: '#0f172a'
    },
    closeBtn: {
        width: '40px',
        height: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f1f5f9',
        border: 'none',
        borderRadius: '10px',
        color: '#64748b',
        cursor: 'pointer',
        transition: 'all 0.2s'
    },
    body: {
        flex: 1,
        overflowY: 'auto',
        padding: '24px'
    },
    errorBox: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '12px 16px',
        backgroundColor: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: '10px',
        fontSize: '14px',
        color: '#ef4444',
        marginBottom: '20px'
    },
    orderSummary: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '14px 16px',
        backgroundColor: '#f8fafc',
        borderRadius: '10px',
        border: '1px solid #e2e8f0',
        marginBottom: '20px'
    },
    orderSummaryLabel: {
        fontSize: '14px',
        fontWeight: '600',
        color: '#64748b'
    },
    orderSummaryValue: {
        fontSize: '14px',
        fontWeight: '500',
        color: '#0f172a'
    },
    fieldGroup: {
        marginBottom: '20px'
    },
    label: {
        display: 'block',
        fontSize: '14px',
        fontWeight: '600',
        color: '#374151',
        marginBottom: '8px'
    },
    typeOptions: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px'
    },
    typeButton: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        padding: '14px 16px',
        backgroundColor: '#f8fafc',
        border: '2px solid #e2e8f0',
        borderRadius: '12px',
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'all 0.2s'
    },
    typeButtonActive: {
        backgroundColor: '#eef2ff',
        borderColor: '#6366f1'
    },
    typeLabel: {
        fontSize: '15px',
        fontWeight: '600'
    },
    typeDesc: {
        fontSize: '13px'
    },
    select: {
        width: '100%',
        padding: '12px 16px',
        fontSize: '15px',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        outline: 'none',
        color: '#0f172a',
        backgroundColor: '#f8fafc',
        boxSizing: 'border-box',
        cursor: 'pointer',
        appearance: 'auto'
    },
    textarea: {
        width: '100%',
        padding: '12px 16px',
        fontSize: '15px',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        outline: 'none',
        color: '#0f172a',
        backgroundColor: '#f8fafc',
        boxSizing: 'border-box',
        resize: 'vertical',
        fontFamily: 'inherit',
        lineHeight: '1.6',
        transition: 'border-color 0.2s'
    },
    charCount: {
        display: 'block',
        textAlign: 'right',
        marginTop: '4px',
        fontSize: '12px',
        color: '#94a3b8'
    },
    imageHint: {
        fontSize: '13px',
        color: '#94a3b8',
        marginTop: '-4px',
        marginBottom: '8px'
    },
    imageUploadArea: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '12px'
    },
    imagePreviewWrapper: {
        position: 'relative',
        width: '80px',
        height: '80px',
        borderRadius: '10px',
        overflow: 'hidden',
        border: '1px solid #e2e8f0'
    },
    imagePreview: {
        width: '100%',
        height: '100%',
        objectFit: 'cover'
    },
    imageRemoveBtn: {
        position: 'absolute',
        top: '4px',
        right: '4px',
        width: '22px',
        height: '22px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0,0,0,0.6)',
        border: 'none',
        borderRadius: '50%',
        color: '#ffffff',
        cursor: 'pointer',
        padding: 0
    },
    imageAddBtn: {
        width: '80px',
        height: '80px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '4px',
        border: '2px dashed #cbd5e1',
        borderRadius: '10px',
        backgroundColor: '#f8fafc',
        cursor: 'pointer',
        transition: 'border-color 0.2s'
    },
    imageAddText: {
        fontSize: '11px',
        color: '#94a3b8',
        fontWeight: '500'
    },
    amountBox: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px 20px',
        backgroundColor: '#eef2ff',
        borderRadius: '12px',
        border: '1px solid #c7d2fe',
        marginBottom: '16px'
    },
    amountLabel: {
        fontSize: '15px',
        fontWeight: '600',
        color: '#475569'
    },
    amountValue: {
        fontSize: '20px',
        fontWeight: '700',
        color: '#6366f1'
    },
    noticeBox: {
        padding: '16px',
        backgroundColor: '#fef3c7',
        borderRadius: '10px',
        border: '1px solid #fde68a'
    },
    noticeText: {
        fontSize: '13px',
        color: '#92400e',
        margin: '0 0 4px 0',
        lineHeight: '1.6'
    },
    footer: {
        display: 'flex',
        gap: '12px',
        padding: '20px 24px',
        borderTop: '1px solid #e2e8f0'
    },
    cancelBtn: {
        flex: 1,
        padding: '14px',
        backgroundColor: '#f1f5f9',
        border: 'none',
        borderRadius: '10px',
        fontSize: '15px',
        fontWeight: '600',
        color: '#64748b',
        cursor: 'pointer',
        transition: 'all 0.2s'
    },
    submitBtn: {
        flex: 1,
        padding: '14px',
        backgroundColor: '#6366f1',
        border: 'none',
        borderRadius: '10px',
        fontSize: '15px',
        fontWeight: '600',
        color: '#ffffff',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        transition: 'all 0.2s'
    },
    submitBtnDisabled: {
        backgroundColor: '#a5b4fc',
        cursor: 'not-allowed'
    }
};

export default RefundRequest;
